// client/src/pages/DocumentPrintPage.js
// The printable view of a manually created invoice, quote, or proforma
// invoice. Lives outside AdminLayout (like the order InvoicePage) so printing
// gives just the document, not the admin sidebar.
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Container, Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, Stack, Divider,
  Alert, CircularProgress, Button, Grid, Paper, Chip, Avatar,
} from '@mui/material';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { AuthContext } from '../context/AuthContext';
import { formatCurrency } from '../utils/currency';
import { API_BASE_URL, getImageUrl } from '../utils/media';
import { DOCUMENT_TYPES, STATUS_COLORS, statusLabel, formatDocumentDate } from '../utils/documents';

const DocumentPrintPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [converting, setConverting] = useState(false);

  const fetchDocument = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/documents/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to load the document.');
      setDoc(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => { fetchDocument(); }, [fetchDocument]);

  const handleConvert = async (toType) => {
    const target = DOCUMENT_TYPES[toType];
    if (!window.confirm(`Create a ${target.label.toLowerCase()} from ${doc.number}? It gets its own number and today's date.`)) return;
    setConverting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/documents/${doc.id}/convert`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ toType }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to convert.');
      navigate(`/documents/${data.id}`);
    } catch (err) {
      alert(err.message);
    } finally {
      setConverting(false);
    }
  };

  if (loading) return <Container sx={{ py: 4 }}><CircularProgress size={28} /></Container>;
  if (error) return <Container sx={{ py: 4 }}><Alert severity="error">{error}</Alert></Container>;
  if (!doc) return null;

  const meta = DOCUMENT_TYPES[doc.type];
  const canConvert = !['declined', 'expired', 'cancelled'].includes(doc.status);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" className="no-print" sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
        <Button component={Link} to={meta.listPath} startIcon={<ArrowBackIcon />}>{meta.plural}</Button>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {!doc.locked && (
            <Button component={Link} to={`/admin/documents/${doc.id}/edit`} startIcon={<EditOutlinedIcon />}>Edit</Button>
          )}
          {canConvert && doc.conversions.map((toType) => (
            <Button key={toType} variant="outlined" onClick={() => handleConvert(toType)} disabled={converting}>
              Convert to {DOCUMENT_TYPES[toType].label}
            </Button>
          ))}
          <Button variant="contained" startIcon={<PrintOutlinedIcon />} onClick={() => window.print()}>Print / Save as PDF</Button>
        </Stack>
      </Stack>

      {(doc.source || doc.convertedTo.length > 0) && (
        <Alert severity="info" className="no-print" sx={{ mb: 2 }}>
          {doc.source && (
            <>Created from {DOCUMENT_TYPES[doc.source.type].label.toLowerCase()}{' '}
              <Link to={`/documents/${doc.source.id}`}>{doc.source.number}</Link>.{' '}
            </>
          )}
          {doc.convertedTo.length > 0 && (
            <>Converted to{' '}
              {doc.convertedTo.map((d, i) => (
                <React.Fragment key={d.id}>
                  {i > 0 && ', '}
                  {DOCUMENT_TYPES[d.type].label.toLowerCase()} <Link to={`/documents/${d.id}`}>{d.number}</Link>
                </React.Fragment>
              ))}.
            </>
          )}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: { xs: 3, sm: 5 } }}>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            {doc.seller?.logo_url && (
              <Avatar src={getImageUrl(doc.seller.logo_url)} variant="rounded" sx={{ width: 64, height: 64, mb: 1, bgcolor: 'transparent' }} />
            )}
            <Typography variant="h5" fontWeight={800} color="primary">{doc.seller?.name}</Typography>
            {doc.seller?.address && (
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>{doc.seller.address}</Typography>
            )}
            {doc.seller?.support_email && <Typography variant="body2" color="text.secondary">{doc.seller.support_email}</Typography>}
            {doc.seller?.vat_number && <Typography variant="body2" color="text.secondary">VAT #: {doc.seller.vat_number}</Typography>}
          </Grid>
          <Grid item xs={12} sm={6} sx={{ textAlign: { sm: 'right' } }}>
            <Typography variant="h4" fontWeight={800}>{meta.heading === 'TAX INVOICE' && !doc.seller?.vat_number ? 'INVOICE' : meta.heading}</Typography>
            <Typography variant="body2" color="text.secondary">{meta.label} #: {doc.number}</Typography>
            <Typography variant="body2" color="text.secondary">Date: {formatDocumentDate(doc.issue_date)}</Typography>
            {doc.due_date && (
              <Typography variant="body2" color="text.secondary">{meta.dueLabel}: {formatDocumentDate(doc.due_date)}</Typography>
            )}
            <Chip label={statusLabel(doc.status)} size="small" color={STATUS_COLORS[doc.status]} sx={{ mt: 1 }} />
          </Grid>
        </Grid>

        <Divider sx={{ mb: 3 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>{doc.type === 'quote' ? 'Prepared For' : 'Bill To'}</Typography>
          <Typography variant="body2">{doc.customer_name}</Typography>
          {doc.customer_company && doc.customer_company !== doc.customer_name && (
            <Typography variant="body2">{doc.customer_company}</Typography>
          )}
          {doc.customer_address && (
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>{doc.customer_address}</Typography>
          )}
          {doc.customer_email && <Typography variant="body2" color="text.secondary">{doc.customer_email}</Typography>}
          {doc.customer_phone && <Typography variant="body2" color="text.secondary">{doc.customer_phone}</Typography>}
          {doc.customer_vat_number && (
            <Typography variant="body2" color="text.secondary">VAT #: {doc.customer_vat_number}</Typography>
          )}
        </Box>

        <Table size="small" sx={{ mb: 3 }}>
          <TableHead>
            <TableRow>
              <TableCell>Description</TableCell>
              <TableCell align="right">Qty</TableCell>
              <TableCell align="right">Unit Price</TableCell>
              <TableCell align="right">Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {doc.items.map((item) => (
              <TableRow key={item.id}>
                <TableCell>{item.description}</TableCell>
                <TableCell align="right">{Number(item.quantity)}</TableCell>
                <TableCell align="right">{formatCurrency(item.unit_price)}</TableCell>
                <TableCell align="right">{formatCurrency(item.line_total)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Stack spacing={1} sx={{ width: { xs: '100%', sm: 300 } }}>
            <Stack direction="row" justifyContent="space-between">
              <Typography color="text.secondary">Subtotal</Typography>
              <Typography>{formatCurrency(doc.subtotal)}</Typography>
            </Stack>
            {Number(doc.discount_amount) > 0 && (
              <Stack direction="row" justifyContent="space-between">
                <Typography color="success.main">Discount</Typography>
                <Typography color="success.main">-{formatCurrency(doc.discount_amount)}</Typography>
              </Stack>
            )}
            <Divider />
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="h6" fontWeight={700}>Total</Typography>
              <Typography variant="h6" fontWeight={700}>{formatCurrency(doc.total_amount)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Includes VAT (15%)</Typography>
              <Typography variant="body2" color="text.secondary">{formatCurrency(doc.vat_amount)}</Typography>
            </Stack>
          </Stack>
        </Box>

        {doc.notes && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>Notes</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{doc.notes}</Typography>
          </Box>
        )}

        <Divider sx={{ my: 3 }} />
        <Typography variant="caption" color="text.secondary">{meta.footer}</Typography>
      </Paper>
    </Container>
  );
};

export default DocumentPrintPage;
